import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faEyeSlash,
  faSpinner,
  faTowerBroadcast,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import type {
  ProviderMigrationItemDto,
  ProviderMigrationProgressDto,
} from '../api/provider-migrations.api';
import type { LiveConnectionState } from '../useMigrationLive';

/**
 * The live operational view of a copy in flight.
 *
 * ── EVERY NUMBER HERE COMES FROM THE SERVER ────────────────────────────────
 * `progress` is recomputed by the backend from the item rows on every read. This
 * component renders it and does nothing else: there is no timer, no counter, no
 * interpolation and no optimistic increment anywhere in this file. If the server
 * says 54 of 59, the screen says 54 of 59 until the server says otherwise. A UI
 * that invented the next number would be lying about a data migration, which is
 * the one place a comforting guess is least acceptable.
 */

function ConnectionBadge({
  connection,
  lastUpdatedAt,
  failureCount,
}: {
  connection: LiveConnectionState;
  lastUpdatedAt: number | null;
  failureCount: number;
}) {
  const seconds = lastUpdatedAt === null ? null : Math.round((Date.now() - lastUpdatedAt) / 1000);

  const map: Record<
    LiveConnectionState,
    { icon: typeof faTowerBroadcast; label: string; tone: string }
  > = {
    live: {
      icon: faTowerBroadcast,
      label: 'Live',
      tone: 'text-emerald-600 border-emerald-500/30 bg-emerald-500/10',
    },
    suspended: {
      icon: faEyeSlash,
      label: 'Paused (tab hidden)',
      tone: 'text-muted-foreground border-border bg-muted',
    },
    degraded: {
      icon: faTriangleExclamation,
      label: `Live updates failing (${failureCount}) — retrying`,
      tone: 'text-amber-700 border-amber-500/30 bg-amber-500/10',
    },
    idle: {
      icon: faCircleCheck,
      label: 'Not running',
      tone: 'text-muted-foreground border-border bg-muted',
    },
  };
  const s = map[connection];

  return (
    <span
      data-testid="live-connection"
      data-state={connection}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        s.tone,
      )}
    >
      <FontAwesomeIcon icon={s.icon} className="h-3 w-3" spin={connection === 'live'} />
      {s.label}
      {seconds !== null && connection !== 'idle' && (
        <span className="text-muted-foreground">· {seconds}s ago</span>
      )}
    </span>
  );
}

function Tile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'good' | 'warn' | 'muted';
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-xl font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-600',
          tone === 'warn' && 'text-amber-600',
          tone === 'muted' && 'text-muted-foreground',
          tone === 'default' && 'text-foreground',
        )}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

export interface LiveProgressPanelProps {
  status: string;
  progress: ProviderMigrationProgressDto | null | undefined;
  /** Fallback counters, used only when the server reports no `progress` block. */
  fallback: { migrated: number; verified: number; failed: number; skipped: number; total: number };
  items: ProviderMigrationItemDto[];
  connection: LiveConnectionState;
  lastUpdatedAt: number | null;
  failureCount: number;
  /** True while the copy is actually executing on the server. */
  running: boolean;
}

export function LiveProgressPanel({
  status,
  progress,
  fallback,
  items,
  connection,
  lastUpdatedAt,
  failureCount,
  running,
}: LiveProgressPanelProps) {
  // Prefer the server's derived block; fall back to the migration's own counters
  // so an older backend still renders honestly rather than showing nothing.
  const total = progress?.total ?? fallback.total;
  const completed = progress?.completed ?? fallback.migrated;
  const verified = progress?.verified ?? fallback.verified;
  const failed = progress?.failed ?? fallback.failed;
  const skipped = progress?.skipped ?? fallback.skipped;
  const percent =
    progress?.percent ??
    (total > 0 ? Math.round(((completed + failed + skipped) / total) * 100) : 0);
  const currentIndex = progress?.currentIndex ?? completed + failed;
  const currentKey = progress?.currentKey ?? null;

  const failedItems = items.filter((i) => i.status === 'FAILED');

  return (
    <div
      className={cn(
        'rounded-xl border p-5',
        running ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
      )}
      data-testid="live-progress-panel"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {running && <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 text-primary" />}
          {running ? 'Copying' : status.replace(/_/g, ' ')}
        </h2>
        <ConnectionBadge
          connection={connection}
          lastUpdatedAt={lastUpdatedAt}
          failureCount={failureCount}
        />
      </div>

      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-medium text-foreground" data-testid="live-progress-count">
          {completed.toLocaleString()} / {total.toLocaleString()} objects
        </span>
        <span
          className={cn(
            'text-sm font-semibold tabular-nums',
            percent >= 100 ? 'text-emerald-600' : 'text-foreground',
          )}
          data-testid="live-progress-percent"
        >
          {percent}%
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Copy progress"
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            percent >= 100 ? 'bg-emerald-500' : 'bg-primary',
          )}
          style={{ width: `${percent}%` }}
        />
      </div>

      {running && total > 0 && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="live-current">
          Current: object{' '}
          <strong className="text-foreground">
            {Math.min(currentIndex, total).toLocaleString()} / {total.toLocaleString()}
          </strong>
          {currentKey !== null && (
            <>
              {' · '}
              <span className="break-all font-mono text-xs">{currentKey}</span>
            </>
          )}
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Completed" value={completed} tone="good" />
        <Tile label="Verified" value={verified} tone="good" />
        <Tile label="Failed" value={failed} tone={failed > 0 ? 'warn' : 'muted'} />
        <Tile label="Skipped" value={skipped} tone="muted" />
      </div>

      {/*
        ── Per-object failures ───────────────────────────────────────────────
        A failed object is not a failed migration, and the operator needs to know
        which objects, why, and whether re-running is safe. It is: the runner
        skips items already COMPLETED and re-verifies an existing destination
        object instead of re-uploading it, so a retry never re-copies work that
        already succeeded.
      */}
      {failedItems.length > 0 && (
        <div
          className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"
          data-testid="failed-items"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-700">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" />
            {failedItems.length} object{failedItems.length === 1 ? '' : 's'} failed
          </p>
          <ul className="mt-2 space-y-1.5">
            {failedItems.slice(0, 10).map((i) => (
              <li key={i.id} className="text-xs">
                <span className="break-all font-mono text-foreground">{i.storageKey}</span>
                {i.errorMessage !== null && (
                  <span className="ml-1 text-muted-foreground">— {i.errorMessage}</span>
                )}
              </li>
            ))}
            {failedItems.length > 10 && (
              <li className="text-xs text-muted-foreground">
                …and {failedItems.length - 10} more. The full list is in the objects table below.
              </li>
            )}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            <strong className="text-foreground">Re-running is safe.</strong> Objects already copied
            and verified are skipped entirely, and an object already present at the destination is
            re-verified by checksum rather than uploaded again — so a retry does not re-copy
            successful work. Nothing was deleted, and no source object was modified.
          </p>
        </div>
      )}
    </div>
  );
}
